import { LayoutGrid, List, SlidersHorizontal } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
// import { HERO_ART } from '@/assets/heroes'; // D-362: the film hero of the shop is replaced by a title bar
// import { PageHero } from '@/components/layout/PageHero';
// import { HeroArt } from '@/components/media/HeroArt';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { ProductGrid } from '@/components/product/ProductGrid';
import { AppLink } from '@/components/ui/AppLink';
import { Button } from '@/components/ui/Button';
import { buttonClasses } from '@/components/ui/button-classes';
import { Container } from '@/components/ui/Container';
import { preloadDrawer } from '@/components/ui/drawer-loader';
import { LazyDrawer } from '@/components/ui/LazyDrawer';
import { Section } from '@/components/ui/Section';
import { Select } from '@/components/ui/Select';
import { clearFilters, countActiveFilters, useFacets, useProductPages } from '@/lib/catalog';
import { pricesShown } from '@/lib/catalog/storefront';
import type { CatalogFilterState } from '@/lib/catalog';
import { interpolate, useFormatters, useT } from '@/lib/i18n';
import { useStoredChoice } from '@/lib/hooks/useStoredChoice';
import { Seo } from '@/lib/seo';
import { scrollStore } from '@/motion/scroll';
import { ActiveFilters } from './ActiveFilters';
import { ColourStrip } from './ColourStrip';
import { content } from './content';
import { FilterPanel } from './FilterPanel';
import { SearchBox } from './SearchBox';
import { useAutoLoadMore } from './useLoadMore';
import { useShopFilters } from './useShopFilters';
import './shop.css';
import './retail.css';
import './shop-redesign.css';

const VIEWS = ['grid', 'list'] as const;

/** `/shop` — the catalogue: search, filters (sidebar on desktop, drawer on phones), sort, results. */
export function ShopPage() {
  const copy = content;
  const t = useT();
  const { formatNumber } = useFormatters();
  const { filters, update, isFiltered } = useShopFilters();
  const [view, setView] = useStoredChoice('sbg.shop.view', VIEWS, 'grid');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const products = useProductPages(filters);
  const facets = useFacets(filters);

  // one array per result set, so that `ProductGrid` (memoised) does not re-render its cards when only the sheet,
  // the sort menu or the view toggle changed
  const items = useMemo(
    () => products.data?.pages.flatMap((page) => page.items) ?? [],
    [products.data],
  );
  const total = products.data?.pages[0]?.total ?? 0;
  const activeCount = countActiveFilters(filters);
  const refreshing =
    products.isPlaceholderData || (products.isFetching && !products.isFetchingNextPage);
  const resultKey = JSON.stringify(filters);

  // A filter changed while the results were scrolled away: bring them back into view.
  // (stable, so that the memoised filter panels do not re-render with every change of the sheet or the sort menu)
  const change = useCallback(
    (next: CatalogFilterState, mode: 'push' | 'replace' = 'push'): void => {
      update(next, mode);
      const top = resultsRef.current?.getBoundingClientRect().top;
      if (top !== undefined && top < 0) {
        scrollStore.scrollTo(Math.max(0, window.scrollY + top - 96));
      }
    },
    [update],
  );

  useAutoLoadMore({
    sentinel: sentinelRef,
    enabled: Boolean(products.hasNextPage) && !products.isFetchingNextPage,
    resultKey,
    onLoad: () => {
      void products.fetchNextPage();
    },
  });

  const sortOptions = [
    { value: 'featured', label: copy.sort.featured },
    { value: 'newest', label: copy.sort.newest },
    // D-363: there are no prices to sort by while every stone says "Ask for the price"
    ...(pricesShown()
      ? [
          { value: 'price_asc', label: copy.sort.priceAsc },
          { value: 'price_desc', label: copy.sort.priceDesc },
        ]
      : []),
    ...(filters.q ? [{ value: 'relevance', label: copy.sort.relevance }] : []),
  ];

  const countLabel =
    total === 1 ? copy.results.one : interpolate(copy.results.many, { count: formatNumber(total) });
  const announcement = products.isSuccess
    ? total === 0
      ? copy.results.announceNone
      : total === 1
        ? copy.results.announceOne
        : interpolate(copy.results.announce, { count: formatNumber(total) })
    : '';

  const panel = (idPrefix: string) => (
    <FilterPanel
      facets={facets.data}
      filters={filters}
      onChange={change}
      copy={copy.filters}
      idPrefix={idPrefix}
    />
  );

  return (
    <div className="shop-page">
      <Seo
        title={copy.seoTitle}
        description={copy.seoDescription}
        path="/shop"
        noindex={isFiltered}
      />
      {/* D-362: a slim storefront title bar instead of the film hero. The hero is kept here for reference.
      <PageHero
        compact
        breadcrumbs={[{ label: t('nav.home'), to: '/' }, { label: t('nav.collection') }]}
        eyebrow={copy.eyebrow}
        title={copy.title}
        lead={copy.lead}
        className="film-hero film-hero--compact on-dark"
        media={<HeroArt art={HERO_ART.shop} focusY={90} />}
      />
      */}
      <header className="shop-head retail">
        <Container>
          <Breadcrumbs
            items={[{ label: t('nav.home'), to: '/' }, { label: t('nav.collection') }]}
          />
          <div className="shop-head__row">
            <div className="shop-head__copy">
              <p className="shop-head__eyebrow">{copy.eyebrow}</p>
              <h1 className="shop-head__title">{copy.title}</h1>
              <p className="shop-head__lead">{copy.lead}</p>
            </div>
            <ColourStrip
              facets={facets.data}
              filters={filters}
              onChange={change}
              label={copy.byColour}
            />
          </div>
        </Container>
      </header>

      <Section className="shop retail" tone="bg" divider={false}>
        <div className="shop__layout">
          <aside className="shop__filters" aria-label={copy.filters.title} data-lenis-prevent="">
            {panel('side')}
          </aside>

          <div className="shop__main">
            <div className="shop__toolbar">
              <SearchBox
                value={filters.q ?? ''}
                onCommit={(q) => {
                  const next: CatalogFilterState = { ...filters };
                  if (q) next.q = q;
                  else {
                    delete next.q;
                    if (next.sort === 'relevance') next.sort = 'featured';
                  }
                  change(next, 'replace');
                }}
                copy={copy.search}
              />
              <div className="shop__controls">
                <Button
                  variant="glass"
                  size="sm"
                  className="shop__filter-button"
                  onPointerEnter={preloadDrawer}
                  onFocus={preloadDrawer}
                  onClick={() => {
                    setDrawerOpen(true);
                  }}
                >
                  <SlidersHorizontal size={16} aria-hidden="true" />
                  {copy.filters.button}
                  {activeCount > 0 ? <span className="shop__badge">{activeCount}</span> : null}
                </Button>
                <div className="shop__sort">
                  <span id="shop-sort-label" className="shop__sort-label">
                    {copy.sort.label}
                  </span>
                  <Select
                    aria-labelledby="shop-sort-label"
                    value={filters.sort}
                    options={sortOptions}
                    onValueChange={(sort) => {
                      change({ ...filters, sort: sort as CatalogFilterState['sort'] });
                    }}
                  />
                </div>
                <div className="shop__view" role="group" aria-label={copy.view.label}>
                  <button
                    type="button"
                    className="shop__view-button"
                    aria-pressed={view === 'grid'}
                    aria-label={copy.view.grid}
                    onClick={() => {
                      setView('grid');
                    }}
                  >
                    <LayoutGrid size={18} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="shop__view-button"
                    aria-pressed={view === 'list'}
                    aria-label={copy.view.list}
                    onClick={() => {
                      setView('list');
                    }}
                  >
                    <List size={18} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>

            <ActiveFilters facets={facets.data} filters={filters} onChange={change} copy={copy} />

            <div ref={resultsRef} className="shop__results" aria-busy={refreshing || undefined}>
              <h2 className="sr-only">{copy.results.heading}</h2>
              <p className="shop__count t-small text-fg-muted">
                {products.isSuccess ? countLabel : ' '}
                {products.isSuccess && items.length < total ? (
                  <span>
                    {' · '}
                    {interpolate(copy.results.showing, {
                      shown: formatNumber(items.length),
                      total: formatNumber(total),
                    })}
                  </span>
                ) : null}
              </p>
              <p className="sr-only" role="status" aria-live="polite">
                {announcement}
              </p>

              {products.isError && !products.data ? (
                <div className="shop__state" role="alert">
                  <h2 className="t-h3">{copy.error.title}</h2>
                  <p className="t-body text-fg-muted">{copy.error.body}</p>
                  <Button
                    onClick={() => {
                      void products.refetch();
                    }}
                  >
                    {copy.error.retry}
                  </Button>
                </div>
              ) : products.isSuccess && items.length === 0 ? (
                <div className="shop__state">
                  <h2 className="t-h3">{copy.empty.title}</h2>
                  <p className="t-body text-fg-muted">{copy.empty.body}</p>
                  <div className="shop__state-actions">
                    {activeCount > 0 ? (
                      <Button
                        onClick={() => {
                          change(clearFilters(filters));
                        }}
                      >
                        {copy.empty.clear}
                      </Button>
                    ) : null}
                    <AppLink
                      to={{ pathname: '/contact-us', hash: '#enquiry' }}
                      className={buttonClasses({ variant: 'glass' })}
                    >
                      {copy.empty.ask}
                    </AppLink>
                  </div>
                </div>
              ) : (
                <div className="shop__grid" data-refreshing={refreshing || undefined}>
                  <ProductGrid
                    products={items}
                    view={view}
                    loading={products.isPending || products.isFetchingNextPage}
                    skeletonCount={products.isPending ? 8 : 4}
                    // only the first photograph is the LCP: it alone is eager with a high fetch priority (and is
                    // preloaded by the pre-render); on a phone the next ones are below the first screen and lazy
                    priorityCount={1}
                    sizes="(min-width: 1280px) 15vw, (min-width: 1024px) 17vw, (min-width: 600px) 24vw, 50vw"
                  />
                  <div ref={sentinelRef} className="shop__sentinel" aria-hidden="true" />
                  {products.hasNextPage ? (
                    <div className="shop__more">
                      <Button
                        variant="outline"
                        loading={products.isFetchingNextPage}
                        onClick={() => {
                          void products.fetchNextPage();
                        }}
                      >
                        {products.isFetchingNextPage
                          ? copy.results.loadingMore
                          : copy.results.loadMore}
                      </Button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </Section>

      <LazyDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title={copy.filters.title}
        description={copy.filters.description}
        footer={
          <>
            <Button
              variant="outline"
              disabled={activeCount === 0}
              onClick={() => {
                change(clearFilters(filters));
              }}
            >
              {copy.filters.clearAll}
            </Button>
            <Button
              className="flex-1"
              onClick={() => {
                setDrawerOpen(false);
              }}
            >
              {interpolate(copy.filters.apply, { count: formatNumber(total) })}
            </Button>
          </>
        }
      >
        {panel('drawer')}
      </LazyDrawer>
    </div>
  );
}
